import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { Children, isValidElement } from 'react';
import { cn } from '../cn.js';
import { IconClose } from '../icons.js';
import { IconButton } from './icon-button.js';

/**
 * A modal dialog. Base UI owns the focus trap, the scroll lock, the escape key, and the ARIA
 * wiring; this file owns only how it looks, in tokens.
 */
export const Dialog = BaseDialog.Root;
export const DialogTrigger = BaseDialog.Trigger;
export const DialogClose = BaseDialog.Close;

/**
 * The scrim behind a dialog.
 *
 * **`DialogContent` renders this itself**, inside the portal, so a screen composing a dialog must
 * not render a second one. Four did — two scrims over one dialog, the outer one outside the portal —
 * which is at best a double darkening and at worst a stray `Dialog.Backdrop` in the part of the page
 * Base UI makes inert while a dialog is open. Removed from all four.
 *
 * It stays exported because the token pages show it, and because a non-modal surface may want the
 * same scrim; it is not part of composing a dialog.
 */
export function DialogBackdrop({ className, ...props }: BaseDialog.Backdrop.Props) {
  return (
    <BaseDialog.Backdrop
      className={cn(
        'fixed inset-0 z-(--elevation-overlay) bg-(--color-overlay-scrim)',
        'transition-opacity duration-(--motion-duration-normal) ease-(--motion-easing-standard)',
        'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
        'motion-reduce:transition-none',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogContentProps extends BaseDialog.Popup.Props {
  size?: 'sm' | 'md' | 'lg';
}

const widthBySize = {
  sm: 'w-(--dialog-width-sm)',
  md: 'w-(--dialog-width-md)',
  lg: 'w-(--dialog-width-lg)',
} as const;

export function DialogContent({ className, size = 'md', children, ...props }: DialogContentProps) {
  return (
    <BaseDialog.Portal>
      <DialogBackdrop />
      <BaseDialog.Popup
        className={cn(
          'fixed left-1/2 top-1/2 z-(--elevation-dialog) -translate-x-1/2 -translate-y-1/2',
          // A dialog stays inside the viewport with a gutter: a relationship to the window,
          // not a value a token or a brand could decide.
          // biome-ignore lint: documented exception to the tokenised-class rule
          'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-y-auto',
          'rounded-(--dialog-radius) p-(--dialog-padding)',
          'bg-(--color-surface-elevated) text-(--color-text-primary)',
          'border border-(--color-border) shadow-(--shadow-overlay)',
          /*
           * **It fades. It does not scale.**
           *
           * It used to arrive from `scale-95`, and on a 32rem dialog that means its contents
           * begin about twenty-five pixels narrower than they end: every button and every line of
           * text starts closer together and spreads as the animation runs. The browser also
           * rasterises the whole subtree at 95% and interpolates it up, so the glyphs are
           * resampled and snap at the last frame. Together they read as the contents shuffling
           * into place rather than the dialog arriving — which is what it looked like.
           *
           * Fading alone also makes this the same gesture as every other popup in the system:
           * the select's and the tooltip's have always been opacity only. The backdrop fades on
           * the same duration, so the two read as one surface arriving.
           */
          'transition-opacity duration-(--motion-duration-normal) ease-(--motion-easing-standard)',
          'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
          'motion-reduce:transition-none',
          widthBySize[size],
          className,
        )}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
}

/**
 * The title and description, stacked, with a close button — when there is one — beside them.
 *
 * It was a row of its children with the space between them, which is right for a title beside a
 * close button and wrong for a title beside a description: every dialog in both applications put
 * the two in it directly, and each drew a squeezed two-line title with the sentence about it in a
 * column to its right (M5.5-01). Only this file's story wrapped them in a `div` of their own, which
 * is why it looked right in Storybook. The text now stacks whichever way it is written.
 */
export function DialogHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const items = Children.toArray(children);
  const isClose = (item: React.ReactNode) =>
    isValidElement(item) && item.type === DialogCloseButton;
  return (
    <div
      className={cn('flex items-start justify-between gap-(--space-md) pb-(--space-md)', className)}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-(--space-xs)">
        {items.filter((item) => !isClose(item))}
      </div>
      {items.filter(isClose)}
    </div>
  );
}

export function DialogTitle({ className, ...props }: BaseDialog.Title.Props) {
  return (
    <BaseDialog.Title
      className={cn(
        'text-(length:--font-size-title) font-semibold text-(--color-text-primary)',
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({ className, ...props }: BaseDialog.Description.Props) {
  return (
    <BaseDialog.Description
      className={cn('text-(length:--font-size-body) text-(--color-text-secondary)', className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex justify-end gap-(--space-sm) pt-(--space-lg)', className)}
      {...props}
    />
  );
}

/** The close affordance in the header; every dialog has one, keyboard reachable. */
export function DialogCloseButton({ label = 'Close' }: { label?: string }) {
  return (
    <BaseDialog.Close
      render={(closeProps) => (
        <IconButton {...closeProps} icon={IconClose} label={label} size="sm" />
      )}
    />
  );
}
